/** SUNTRONIX ONE — My Work exception chip.
 *
 *  Floating red badge in the top bar that shows the count of exceptions
 *  currently assigned to the signed-in user.  Overdue items pulse.
 *  Clicking the chip opens a compact popover listing the top items
 *  with links straight into the Exception Centre.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ChevronRight, Timer, Zap } from "lucide-react";

export default function MyWorkChip() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/exception-engine/my-work");
      setItems(data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  const overdue = items.filter((i) => (i.overdue_hours || 0) > 0);
  const count = items.length;
  const hasOverdue = overdue.length > 0;

  if (count === 0) return null;

  return (
    <div className="relative" data-testid="mywork-chip">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11px] font-semibold border transition
          ${hasOverdue
            ? "bg-red-600 text-white border-red-700 shadow-[0_0_0_0_rgba(220,38,38,0.55)] animate-[pulse-ring_1.6s_ease-out_infinite]"
            : "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"}`}
        data-testid="mywork-chip-btn"
        aria-label={`${count} exception${count === 1 ? "" : "s"} assigned to me`}
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        {count} to fix
        {hasOverdue && <span className="text-[10px] uppercase tracking-wider">· {overdue.length} overdue</span>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[45]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1.5 w-[380px] bg-white rounded-lg shadow-xl border border-slate-200 z-[46] max-h-[480px] overflow-y-auto"
                data-testid="mywork-chip-popover">
            <div className="p-3 border-b border-slate-100 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My exceptions</div>
              <button
                className="text-[11px] text-slate-600 hover:underline"
                onClick={() => { setOpen(false); navigate("/admin/exceptions"); }}
                data-testid="mywork-chip-open-all">
                View all →
              </button>
            </div>
            <div className="divide-y divide-slate-100">
              {items.slice(0, 8).map((it) => {
                const od = it.overdue_hours > 0;
                return (
                  <button
                    key={it.id}
                    onClick={() => { setOpen(false); navigate("/admin/exceptions"); }}
                    className="w-full text-left p-3 hover:bg-slate-50 transition"
                    data-testid={`mywork-chip-item-${it.id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border font-semibold ${
                        it.severity === "P0" ? "bg-red-100 text-red-800 border-red-300"
                        : it.severity === "P1" ? "bg-red-50 text-red-700 border-red-200"
                        : it.severity === "P2" ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {it.severity}
                      </span>
                      {it.blocking === "hard" && (
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-red-50 text-red-800 border-red-300">
                          Hard block
                        </span>
                      )}
                      {od && (
                        <span className="ml-auto text-[10px] text-red-700 font-semibold flex items-center gap-0.5">
                          <Timer className="h-3 w-3" /> {it.overdue_hours.toFixed(1)}h overdue
                        </span>
                      )}
                    </div>
                    <div className="text-[13px] text-slate-900 font-medium mt-1 line-clamp-1">{it.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{it.next_action}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                      {it.entity_kind} · {it.entity_code || it.entity_id.slice(0,8)}
                    </div>
                  </button>
                );
              })}
              {items.length > 8 && (
                <div className="p-2 text-center text-[11px] text-slate-500">
                  +{items.length - 8} more — <button
                    onClick={() => { setOpen(false); navigate("/admin/exceptions"); }}
                    className="text-slate-700 hover:underline">open Exception Centre</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes pulse-ring {
          0%   { box-shadow: 0 0 0 0 rgba(220,38,38, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(220,38,38, 0);   }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38, 0);      }
        }
      `}</style>
    </div>
  );
}
